import React, { useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CLoadingButton,
  CMultiSelect,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilWarning } from '@coreui/icons'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import { isSetUp } from './bonusCardUtils'

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Bonus Card Name is required'),
  description: Yup.string().required('Description is required'),
})

const sameIds = (a, b) =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',')

// Set up or edit one round's bonus card. `round` is the row from the rounds table; the panel
// is open while it is set.
const BonusCardForm = ({ round, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [detail, setDetail] = useState(null)
  const [options, setOptions] = useState([])
  const [selectedIds, setSelectedIds] = useState([])

  const roundNumber = round?.round
  const editing = isSetUp(round)

  useEffect(() => {
    if (!roundNumber) return
    setLoading(true)
    setDetail(null)
    setOptions([])
    setSelectedIds([])
    Promise.all([
      CommonService.bonusCardSelectedPlayer(roundNumber),
      CommonService.getBonusCardDetail(roundNumber),
    ])
      .then(([optionsResult, detailResult]) => {
        const list = optionsResult.data || []
        setOptions(list)
        setSelectedIds(list.filter((o) => o.selected).map((o) => o.value))
        setDetail(detailResult.data)
      })
      .catch(() => ToastComponent('Could not load the bonus card', 'error'))
      .finally(() => setLoading(false))
  }, [roundNumber])

  const initialIds = useMemo(() => options.filter((o) => o.selected).map((o) => o.value), [options])
  const playersChanged = !sameIds(initialIds, selectedIds)

  const formik = useFormik({
    initialValues: {
      title: detail?.name ?? '',
      description: detail?.description ?? '',
      player: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      // The API replaces the card's players whenever it is sent a list, and that wipes their
      // bonus points - so only send one when the selection actually changed.
      data.player = playersChanged ? selectedIds : []
      data.round = roundNumber
      setLoader(true)
      CommonService.saveBonusCard(data)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            onSaved()
            onClose()
          } else {
            ToastComponent(res.message, 'error')
          }
        })
        .catch((e) =>
          ToastComponent(e.response?.data?.message || 'Could not save the bonus card', 'error'),
        )
        .finally(() => setLoader(false))
    },
  })

  // Two rounds without a card share the same blank initial values, so reinitialising alone
  // would carry text typed for one round into the next.
  useEffect(() => {
    if (roundNumber) formik.resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundNumber])

  const fieldError = (name) => (formik.touched[name] ? formik.errors[name] : null)

  return (
    <COffcanvas placement="end" visible={!!round} onHide={onClose} backdrop style={{ width: 480 }}>
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>
            {editing ? 'Edit' : 'Set Up'} Round {roundNumber} Bonus Card
          </COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Name the card and choose the players on it.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading bonus card...</span>
          </div>
        ) : (
          <CForm onSubmit={formik.handleSubmit}>
            <CRow className="g-3 align-content-start">
              <CCol xs={12}>
                <CFormLabel htmlFor="title">Bonus Card Name *</CFormLabel>
                <CFormInput
                  id="title"
                  name="title"
                  placeholder="e.g. Local Heroes"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!fieldError('title')}
                />
                {fieldError('title') && (
                  <CFormFeedback invalid>{fieldError('title')}</CFormFeedback>
                )}
              </CCol>

              <CCol xs={12}>
                <CFormLabel htmlFor="description">Description *</CFormLabel>
                <CFormTextarea
                  id="description"
                  name="description"
                  rows={3}
                  placeholder="What members need to know about this card"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!fieldError('description')}
                />
                {fieldError('description') && (
                  <CFormFeedback invalid>{fieldError('description')}</CFormFeedback>
                )}
              </CCol>

              <CCol xs={12}>
                <CFormLabel className="d-flex justify-content-between">
                  <span>Players</span>
                  <span className="text-medium-emphasis fw-normal">
                    {selectedIds.length} selected
                  </span>
                </CFormLabel>
                <CMultiSelect
                  options={options}
                  selectionType="tags"
                  placeholder="Search players..."
                  searchNoResultsLabel="No players found"
                  onChange={(selected) =>
                    setSelectedIds(Array.isArray(selected) ? selected.map((o) => o.value) : [])
                  }
                />
                {editing && playersChanged && (
                  <CAlert color="warning" className="d-flex gap-2 border-0 small mt-2 mb-0">
                    <CIcon icon={cilWarning} className="flex-shrink-0 mt-1" />
                    <span>
                      Changing the players resets the bonus points already awarded on this card.
                    </span>
                  </CAlert>
                )}
              </CCol>
            </CRow>
          </CForm>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Cancel
        </CButton>
        <CLoadingButton
          color="primary"
          loading={loader}
          disabled={loading}
          onClick={() => formik.handleSubmit()}
        >
          {editing ? 'Save Changes' : 'Create Bonus Card'}
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default BonusCardForm
